/**
 * TerraKronos Zero-Knowledge Encryption Utilities
 * 
 * This module provides client-side encryption using:
 * - Argon2id for password-based key derivation
 * - AES-256-GCM for symmetric encryption (Web Crypto API)
 * - X25519 for key exchange (sharing)
 * - BIP39 for recovery code generation
 * 
 * SECURITY: All encryption/decryption happens client-side.
 * The server NEVER receives plaintext sensitive data or encryption keys.
 */

// Buffer polyfill for browser environment (required by bip39)
import { Buffer } from 'buffer';
if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
}

import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { randomBytes } from '@noble/hashes/utils.js';
import { argon2id } from '@noble/hashes/argon2.js';
import { gcm } from '@noble/ciphers/aes.js';
import * as bip39 from 'bip39';

// ============================================
// Constants
// ============================================

const ARGON2_CONFIG = {
  t: 3,           // iterations
  m: 65536,       // 64 MB memory
  p: 4            // parallelism
};

const ENCRYPTION_VERSION = 1;

// ============================================
// Utility Functions
// ============================================

/**
 * Convert Uint8Array to base64 string
 */
export function toBase64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert base64 string to Uint8Array
 */
export function fromBase64(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Generate cryptographically secure random bytes
 */
export function generateRandomBytes(length) {
  return randomBytes(length);
}

/**
 * Generate a random 32-byte (256-bit) key
 */
export function generateKey() {
  return generateRandomBytes(32);
}

/**
 * Generate a random 12-byte (96-bit) nonce for AES-GCM
 */
export function generateNonce() {
  return generateRandomBytes(12);
}

// ============================================
// Key Derivation (Argon2id)
// ============================================

/**
 * Derive a key from password using Argon2id
 * 
 * @param {string} password - User's password
 * @param {Uint8Array} salt - Random salt (should be stored server-side)
 * @returns {Promise<Uint8Array>} - 256-bit derived key
 */
export async function deriveKeyFromPassword(password, salt) {
  const passwordBytes = new TextEncoder().encode(password);
  const result = argon2id(passwordBytes, salt, {
    ...ARGON2_CONFIG,
    dkLen: 32  // 256-bit output
  });
  return result;
}

/**
 * Generate a salt for Argon2id key derivation
 */
export function generateSalt() {
  return generateRandomBytes(16);
}

// ============================================
// AES-256-GCM Encryption (Web Crypto API)
// ============================================

/**
 * Encrypt data using AES-256-GCM
 * 
 * @param {Uint8Array} plaintext - Data to encrypt
 * @param {Uint8Array} key - 256-bit encryption key
 * @param {Uint8Array} [nonce] - Optional 96-bit nonce (generated if not provided)
 * @returns {Promise<{ ciphertext: Uint8Array, nonce: Uint8Array }>}
 */
export async function encrypt(plaintext, key, nonce = null) {
  const encNonce = nonce || generateNonce();
  
  // Import key for Web Crypto
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
  
  // Encrypt
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: encNonce },
    cryptoKey,
    plaintext
  );
  
  return { ciphertext: new Uint8Array(ciphertext), nonce: encNonce };
}

/**
 * Decrypt data using AES-256-GCM
 * 
 * @param {Uint8Array} ciphertext - Encrypted data (includes auth tag)
 * @param {Uint8Array} key - 256-bit encryption key
 * @param {Uint8Array} nonce - 96-bit nonce used during encryption
 * @returns {Promise<Uint8Array>} - Decrypted plaintext
 */
export async function decrypt(ciphertext, key, nonce) {
  // Import key for Web Crypto
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  
  // Decrypt
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce },
    cryptoKey,
    ciphertext
  );
  
  return new Uint8Array(plaintext);
}

/**
 * Encrypt a string to base64
 */
export async function encryptString(plaintext, key) {
  const encoder = new TextEncoder();
  const plaintextBytes = encoder.encode(plaintext);
  const { ciphertext, nonce } = await encrypt(plaintextBytes, key);
  return {
    ciphertext: toBase64(ciphertext),
    nonce: toBase64(nonce)
  };
}

/**
 * Decrypt a base64 string
 */
export async function decryptString(ciphertextBase64, key, nonceBase64) {
  const ciphertext = fromBase64(ciphertextBase64);
  const nonce = fromBase64(nonceBase64);
  const plaintext = await decrypt(ciphertext, key, nonce);
  const decoder = new TextDecoder();
  return decoder.decode(plaintext);
}

// ============================================
// Key Wrapping (Encrypting keys with other keys)
// ============================================

/**
 * Wrap (encrypt) a key with another key
 * Used to encrypt MEK with PDK, or document keys with MEK
 */
export async function wrapKey(keyToWrap, wrappingKey) {
  const { ciphertext, nonce } = await encrypt(keyToWrap, wrappingKey);
  return {
    wrappedKey: toBase64(ciphertext),
    nonce: toBase64(nonce)
  };
}

/**
 * Unwrap (decrypt) a key
 */
export async function unwrapKey(wrappedKeyBase64, wrappingKey, nonceBase64) {
  const wrappedKey = fromBase64(wrappedKeyBase64);
  const nonce = fromBase64(nonceBase64);
  return await decrypt(wrappedKey, wrappingKey, nonce);
}

// ============================================
// Recovery Code (BIP39 Mnemonic)
// ============================================

/**
 * Generate a 24-word recovery mnemonic
 * Uses 256 bits of entropy for maximum security
 */
export function generateRecoveryCode() {
  const entropy = generateRandomBytes(32); // 256 bits = 24 words
  // Convert Uint8Array to hex string (browser-compatible, no Buffer needed)
  const hexString = Array.from(entropy).map(b => b.toString(16).padStart(2, '0')).join('');
  return bip39.entropyToMnemonic(hexString);
}

/**
 * Validate a recovery mnemonic
 */
export function validateRecoveryCode(mnemonic) {
  return bip39.validateMnemonic(mnemonic);
}

/**
 * Derive a key from recovery mnemonic
 * Uses the mnemonic as a "password" with a fixed recovery salt
 */
export async function deriveKeyFromRecoveryCode(mnemonic) {
  // Use a fixed, known salt for recovery (different from password salt)
  // This allows recovery without needing to store a separate salt
  const recoverySalt = new TextEncoder().encode('TerraKronos-Recovery-Salt-v1');
  return deriveKeyFromPassword(mnemonic, recoverySalt);
}

// ============================================
// X25519 Key Exchange (for Sharing)
// ============================================

/**
 * Generate an X25519 keypair for key exchange
 */
export function generateKeyPair() {
  const privateKey = generateRandomBytes(32);
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

/**
 * Perform X25519 key exchange to derive a shared secret
 */
export function deriveSharedSecret(privateKey, publicKey) {
  return x25519.getSharedSecret(privateKey, publicKey);
}

/**
 * Derive an encryption key from a shared secret using HKDF
 */
export function deriveKeyFromSharedSecret(sharedSecret, info = 'TerraKronos-Share') {
  const infoBytes = new TextEncoder().encode(info);
  return hkdf(sha256, sharedSecret, undefined, infoBytes, 32);
}

// ============================================
// High-Level Encryption Operations
// ============================================

/**
 * Initialize encryption for a new user
 * Called during registration
 * 
 * @param {string} password - User's chosen password
 * @returns {Promise<Object>} - All encryption data to store
 */
export async function initializeUserEncryption(password) {
  // Generate salt for password derivation
  const salt = generateSalt();
  
  // Derive Password-Derived Key (PDK) from password
  const pdk = await deriveKeyFromPassword(password, salt);
  
  // Generate Master Encryption Key (MEK)
  const mek = generateKey();
  
  // Generate recovery code and derive recovery key
  const recoveryCode = generateRecoveryCode();
  const recoveryKey = await deriveKeyFromRecoveryCode(recoveryCode);
  
  // Wrap MEK with PDK (for normal login)
  const { wrappedKey: encryptedMek, nonce: mekNonce } = await wrapKey(mek, pdk);
  
  // Wrap MEK with recovery key (for password reset)
  const { wrappedKey: recoveryEncryptedMek, nonce: recoveryMekNonce } = await wrapKey(mek, recoveryKey);
  
  // Generate X25519 keypair for sharing
  const { privateKey: sharingPrivateKey, publicKey: sharingPublicKey } = generateKeyPair();
  
  // Wrap sharing private key with MEK
  const { wrappedKey: encryptedSharingPrivateKey, nonce: sharingKeyNonce } = await wrapKey(sharingPrivateKey, mek);
  
  return {
    // To store on server
    serverData: {
      salt: toBase64(salt),
      encrypted_mek: encryptedMek,
      mek_nonce: mekNonce,
      recovery_encrypted_mek: recoveryEncryptedMek,
      recovery_mek_nonce: recoveryMekNonce,
      sharing_public_key: toBase64(sharingPublicKey),
      encrypted_sharing_private_key: encryptedSharingPrivateKey,
      sharing_key_nonce: sharingKeyNonce,
      encryption_version: ENCRYPTION_VERSION
    },
    // To show to user ONCE (never store)
    recoveryCode,
    // For immediate use in session (never transmit)
    mek,
    sharingPrivateKey
  };
}

/**
 * Initialize encryption for Google OAuth users (no password needed)
 * Auto-unlocks on login, only needs recovery code for disaster recovery
 * 
 * @returns {Promise<Object>} - All encryption data to store
 */
export async function initializeEncryptionNoPassword() {
  // Generate Master Encryption Key (MEK)
  const mek = generateKey();
  
  // Generate recovery code and derive recovery key
  const recoveryCode = generateRecoveryCode();
  const recoveryKey = await deriveKeyFromRecoveryCode(recoveryCode);
  
  // Generate a session unlock key - stored in browser for auto-unlock
  const sessionUnlockKey = generateKey();
  const sessionSalt = generateSalt();
  
  // Wrap MEK with session unlock key (for auto-unlock on login)
  const { wrappedKey: sessionEncryptedMek, nonce: sessionMekNonce } = await wrapKey(mek, sessionUnlockKey);
  
  // Wrap MEK with recovery key (for recovery)
  const { wrappedKey: recoveryEncryptedMek, nonce: recoveryMekNonce } = await wrapKey(mek, recoveryKey);
  
  // Generate X25519 keypair for sharing
  const { privateKey: sharingPrivateKey, publicKey: sharingPublicKey } = generateKeyPair();
  
  // Wrap sharing private key with MEK
  const { wrappedKey: encryptedSharingPrivateKey, nonce: sharingKeyNonce } = await wrapKey(sharingPrivateKey, mek);
  
  return {
    serverData: {
      salt: toBase64(sessionSalt),
      encrypted_mek: sessionEncryptedMek,
      mek_nonce: sessionMekNonce,
      recovery_encrypted_mek: recoveryEncryptedMek,
      recovery_mek_nonce: recoveryMekNonce,
      sharing_public_key: toBase64(sharingPublicKey),
      encrypted_sharing_private_key: encryptedSharingPrivateKey,
      sharing_key_nonce: sharingKeyNonce,
      encryption_version: ENCRYPTION_VERSION,
      no_password_mode: true
    },
    recoveryCode,
    mek,
    sharingPrivateKey,
    sessionUnlockKey: toBase64(sessionUnlockKey)
  };
}

/**
 * Unlock encryption for no-password mode using stored session key
 */
export async function unlockEncryptionNoPassword(sessionUnlockKeyBase64, encryptionData) {
  const sessionUnlockKey = fromBase64(sessionUnlockKeyBase64);
  
  const mek = await unwrapKey(
    encryptionData.encrypted_mek,
    sessionUnlockKey,
    encryptionData.mek_nonce
  );
  
  return mek;
}

/**
 * Unlock encryption on login
 * 
 * @param {string} password - User's password
 * @param {Object} encryptionData - Data from server (salt, encrypted_mek, etc.)
 * @returns {Promise<Uint8Array>} - Decrypted MEK for session use
 */
export async function unlockEncryption(password, encryptionData) {
  const salt = fromBase64(encryptionData.salt);
  const pdk = await deriveKeyFromPassword(password, salt);
  
  const mek = await unwrapKey(
    encryptionData.encrypted_mek,
    pdk,
    encryptionData.mek_nonce
  );
  
  return mek;
}

/**
 * Recover encryption using recovery code
 * 
 * @param {string} recoveryCode - 24-word mnemonic
 * @param {Object} encryptionData - Data from server
 * @returns {Promise<Uint8Array>} - Decrypted MEK
 */
export async function recoverEncryption(recoveryCode, encryptionData) {
  const recoveryKey = await deriveKeyFromRecoveryCode(recoveryCode);
  
  const mek = await unwrapKey(
    encryptionData.recovery_encrypted_mek,
    recoveryKey,
    encryptionData.recovery_mek_nonce
  );
  
  return mek;
}

/**
 * Re-encrypt MEK with new password (after password change/reset)
 * 
 * @param {Uint8Array} mek - Current MEK (decrypted)
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - New encryption data for server
 */
export async function reEncryptWithNewPassword(mek, newPassword) {
  const newSalt = generateSalt();
  const newPdk = await deriveKeyFromPassword(newPassword, newSalt);
  const { wrappedKey: newEncryptedMek, nonce: newMekNonce } = await wrapKey(mek, newPdk);
  
  return {
    salt: toBase64(newSalt),
    encrypted_mek: newEncryptedMek,
    mek_nonce: newMekNonce
  };
}

// ============================================
// Field Encryption (for sensitive transaction fields)
// ============================================

/**
 * Encrypt a single field value
 */
export async function encryptField(value, mek) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const stringValue = String(value);
  return await encryptString(stringValue, mek);
}

/**
 * Decrypt a single field value
 */
export async function decryptField(encryptedData, mek) {
  if (!encryptedData || !encryptedData.ciphertext) {
    return null;
  }
  try {
    return await decryptString(encryptedData.ciphertext, mek, encryptedData.nonce);
  } catch (e) {
    console.error('Failed to decrypt field:', e);
    return null;
  }
}

/**
 * Encrypt multiple fields in an object
 * 
 * @param {Object} data - Object with field values
 * @param {string[]} fieldsToEncrypt - Array of field names to encrypt
 * @param {Uint8Array} mek - Master Encryption Key
 * @returns {Promise<Object>} - Object with encrypted fields
 */
export async function encryptFields(data, fieldsToEncrypt, mek) {
  const result = { ...data };
  const encrypted = {};
  
  for (const field of fieldsToEncrypt) {
    if (data[field] !== undefined) {
      encrypted[field] = await encryptField(data[field], mek);
      delete result[field]; // Remove plaintext
    }
  }
  
  result.encrypted_fields = encrypted;
  return result;
}

/**
 * Decrypt multiple fields in an object
 */
export async function decryptFields(data, fieldsToDecrypt, mek) {
  const result = { ...data };
  const encrypted = data.encrypted_fields || {};
  
  for (const field of fieldsToDecrypt) {
    if (encrypted[field]) {
      result[field] = await decryptField(encrypted[field], mek);
    }
  }
  
  delete result.encrypted_fields;
  return result;
}

// ============================================
// Transaction Key Management (Phase 2 E2EE)
// ============================================

/**
 * Generate a new Transaction Key for a transaction.
 * Called when owner uploads the first document.
 * 
 * @param {Uint8Array} ownerMek - Owner's Master Encryption Key
 * @returns {Object} - TK data to send to server + raw TK for local use
 */
export async function generateTransactionKey(ownerMek) {
  // Generate random Transaction Key
  const transactionKey = generateKey();
  
  // Wrap TK with owner's MEK for storage
  const { wrappedKey: encryptedTk, nonce: tkNonce } = await wrapKey(transactionKey, ownerMek);
  
  return {
    // Data to send to server
    serverData: {
      encrypted_transaction_key: encryptedTk,
      transaction_key_nonce: tkNonce
    },
    // Raw TK for local use (encrypt documents immediately)
    transactionKey
  };
}

/**
 * Unwrap a Transaction Key using owner's MEK.
 * 
 * @param {string} encryptedTk - Base64 wrapped Transaction Key
 * @param {string} tkNonce - Base64 nonce
 * @param {Uint8Array} mek - Master Encryption Key
 * @returns {Promise<Uint8Array>} - Decrypted Transaction Key
 */
export async function unwrapTransactionKey(encryptedTk, tkNonce, mek) {
  return await unwrapKey(encryptedTk, mek, tkNonce);
}

/**
 * Wrap a Transaction Key for another user using their public key.
 * Used when granting document access to TCs or shared users.
 * 
 * @param {Uint8Array} transactionKey - Raw Transaction Key
 * @param {string} recipientPublicKeyB64 - Recipient's sharing public key (Base64)
 * @param {Uint8Array} senderPrivateKey - Sender's sharing private key
 * @returns {Object} - Wrapped key data to store
 */
export async function wrapTransactionKeyForUser(transactionKey, recipientPublicKeyB64, senderPrivateKey) {
  const recipientPublicKey = fromBase64(recipientPublicKeyB64);
  
  // Derive shared secret using X25519
  const sharedSecret = deriveSharedSecret(senderPrivateKey, recipientPublicKey);
  const derivedWrapKey = deriveKeyFromSharedSecret(sharedSecret);
  
  // Wrap TK with shared secret
  const { wrappedKey, nonce } = await wrapKey(transactionKey, derivedWrapKey);
  
  return {
    wrapped_transaction_key: wrappedKey,
    key_nonce: nonce
  };
}

/**
 * Unwrap a Transaction Key that was shared with us.
 * 
 * @param {string} wrappedTk - Base64 wrapped Transaction Key
 * @param {string} tkNonce - Base64 nonce
 * @param {string} senderPublicKeyB64 - Sender's sharing public key (Base64)
 * @param {Uint8Array} recipientPrivateKey - Recipient's sharing private key
 * @returns {Promise<Uint8Array>} - Decrypted Transaction Key
 */
export async function unwrapSharedTransactionKey(wrappedTk, tkNonce, senderPublicKeyB64, recipientPrivateKey) {
  const senderPublicKey = fromBase64(senderPublicKeyB64);
  
  // Derive shared secret
  const sharedSecret = deriveSharedSecret(recipientPrivateKey, senderPublicKey);
  const unwrapKeyDerived = deriveKeyFromSharedSecret(sharedSecret);
  
  // Unwrap TK
  return await unwrapKey(wrappedTk, unwrapKeyDerived, tkNonce);
}

/**
 * Encrypt a document using Transaction Key.
 * Each document gets a unique Document Key (DK) which is then wrapped with TK.
 * 
 * @param {ArrayBuffer} fileData - Raw file data
 * @param {Uint8Array} transactionKey - Transaction Key
 * @returns {Promise<Object>} - Encrypted file and metadata
 */
export async function encryptDocumentWithTK(fileData, transactionKey) {
  // Generate unique Document Key for this file
  const documentKey = generateKey();
  
  // Encrypt file with DK
  const fileBytes = new Uint8Array(fileData);
  const { ciphertext: encryptedFile, nonce: fileNonce } = await encrypt(fileBytes, documentKey);
  
  // Wrap DK with TK
  const { wrappedKey: encryptedDk, nonce: dkNonce } = await wrapKey(documentKey, transactionKey);
  
  return {
    encryptedFile,
    encryptedDocumentKey: encryptedDk,
    fileNonce: toBase64(fileNonce),
    dkNonce
  };
}

/**
 * Decrypt a document using Transaction Key.
 * 
 * @param {Uint8Array} encryptedFile - Encrypted file data
 * @param {string} encryptedDk - Base64 wrapped Document Key
 * @param {string} fileNonce - Base64 nonce for file encryption
 * @param {string} dkNonce - Base64 nonce for DK wrapping
 * @param {Uint8Array} transactionKey - Transaction Key
 * @returns {Promise<Uint8Array>} - Decrypted file data
 */
export async function decryptDocumentWithTK(encryptedFile, encryptedDk, fileNonce, dkNonce, transactionKey) {
  // Unwrap Document Key
  const documentKey = await unwrapKey(encryptedDk, transactionKey, dkNonce);
  
  // Decrypt file
  const fileNonceBytes = fromBase64(fileNonce);
  return await decrypt(encryptedFile, documentKey, fileNonceBytes);
}

/**
 * Re-wrap the same Document Key from MEK to Transaction Key without changing ciphertext on disk.
 * Legacy MEK documents store file bytes encrypted with DK; only how DK is wrapped changes.
 */
export async function rewrapDocumentKeyFromMekToTk(encryptedDk, dkNonce, fileNonce, mek, transactionKey) {
  const documentKey = await unwrapKey(encryptedDk, mek, dkNonce);
  const { wrappedKey: newEncryptedDk, nonce: newDkNonce } = await wrapKey(documentKey, transactionKey);
  return {
    encrypted_dk: newEncryptedDk,
    dk_nonce: newDkNonce,
    file_nonce: fileNonce,
  };
}

// ============================================
// Document Encryption
// ============================================

/**
 * Create an external share with password-protected Transaction Key.
 * Used when sharing documents with external clients who don't have accounts.
 * 
 * @param {Uint8Array} transactionKey - Transaction Key to wrap
 * @param {string} password - Access password chosen by owner
 * @returns {Object} - Encrypted TK and password hash for storage
 */
export async function createExternalDocumentShare(transactionKey, password) {
  // Create password hash for verification
  const passwordBytes = new TextEncoder().encode(password);
  const passwordHashBytes = sha256(passwordBytes);
  const passwordHash = Array.from(passwordHashBytes).map(b => b.toString(16).padStart(2, '0')).join('');
  
  // Create encryption key from password
  const encryptKey = sha256(new TextEncoder().encode('TerraKronos-External-' + password));
  
  // Encrypt the Transaction Key
  const nonce = generateKey().slice(0, 12); // 12-byte nonce for GCM
  const aes = gcm(encryptKey, nonce);
  const encryptedTK = aes.encrypt(transactionKey);
  
  return {
    encryptedTransactionKey: toBase64(encryptedTK),
    keyNonce: toBase64(nonce),
    passwordHash
  };
}

/**
 * Decrypt Transaction Key from external share.
 * Used by clients accessing documents via share link.
 * 
 * @param {string} encryptedTkB64 - Base64 encrypted Transaction Key
 * @param {string} nonceB64 - Base64 nonce
 * @param {string} password - Access password
 * @returns {Uint8Array} - Decrypted Transaction Key
 */
export async function decryptExternalDocumentShare(encryptedTkB64, nonceB64, password) {
  const encryptKey = sha256(new TextEncoder().encode('TerraKronos-External-' + password));
  const encryptedTK = fromBase64(encryptedTkB64);
  const nonce = fromBase64(nonceB64);
  
  const aes = gcm(encryptKey, nonce);
  return aes.decrypt(encryptedTK);
}

/**
 * Encrypt a file/document
 * 
 * @param {ArrayBuffer} fileData - Raw file data
 * @param {Uint8Array} mek - Master Encryption Key
 * @returns {Promise<Object>} - Encrypted file data and metadata
 */
export async function encryptDocument(fileData, mek) {
  // Generate a unique Document Key (DK) for this file
  const documentKey = generateKey();
  
  // Encrypt the file with the document key
  const fileBytes = new Uint8Array(fileData);
  const { ciphertext: encryptedFile, nonce: fileNonce } = await encrypt(fileBytes, documentKey);
  
  // Wrap the document key with MEK
  const { wrappedKey: encryptedDocumentKey, nonce: dkNonce } = await wrapKey(documentKey, mek);
  
  return {
    encryptedFile,
    encryptedDocumentKey,
    fileNonce: toBase64(fileNonce),
    dkNonce
  };
}

/**
 * Decrypt a file/document
 * 
 * @param {Uint8Array} encryptedFile - Encrypted file data
 * @param {string} encryptedDocumentKey - Base64 wrapped document key
 * @param {string} fileNonce - Base64 nonce for file encryption
 * @param {string} dkNonce - Base64 nonce for document key wrapping
 * @param {Uint8Array} mek - Master Encryption Key
 * @returns {Promise<Uint8Array>} - Decrypted file data
 */
export async function decryptDocument(encryptedFile, encryptedDocumentKey, fileNonce, dkNonce, mek) {
  // Unwrap the document key
  const documentKey = await unwrapKey(encryptedDocumentKey, mek, dkNonce);
  
  // Decrypt the file
  const fileNonceBytes = fromBase64(fileNonce);
  return await decrypt(encryptedFile, documentKey, fileNonceBytes);
}

// ============================================
// Sharing Encryption
// ============================================

/**
 * Create encrypted share for a document
 * 
 * @param {string} encryptedDocumentKey - Document key (encrypted by owner's MEK)
 * @param {string} dkNonce - Nonce for document key
 * @param {Uint8Array} ownerMek - Owner's MEK
 * @param {Uint8Array} recipientPublicKey - Recipient's X25519 public key
 * @returns {Promise<Object>} - Share data to store
 */
export async function createShare(encryptedDocumentKey, dkNonce, ownerMek, recipientPublicKey) {
  // Unwrap the document key
  const documentKey = await unwrapKey(encryptedDocumentKey, ownerMek, dkNonce);
  
  // Generate ephemeral keypair for this share
  const { privateKey: ephemeralPrivate, publicKey: ephemeralPublic } = generateKeyPair();
  
  // Derive shared secret
  const sharedSecret = deriveSharedSecret(ephemeralPrivate, recipientPublicKey);
  const shareKey = deriveKeyFromSharedSecret(sharedSecret);
  
  // Encrypt document key with share key
  const { wrappedKey: shareEncryptedDk, nonce: shareNonce } = await wrapKey(documentKey, shareKey);
  
  return {
    share_encrypted_dk: shareEncryptedDk,
    share_nonce: shareNonce,
    sender_ephemeral_public: toBase64(ephemeralPublic)
  };
}

/**
 * Decrypt a shared document key
 * 
 * @param {string} shareEncryptedDk - Document key encrypted for sharing
 * @param {string} shareNonce - Nonce for share encryption
 * @param {string} senderEphemeralPublic - Sender's ephemeral public key
 * @param {Uint8Array} recipientPrivateKey - Recipient's X25519 private key
 * @returns {Promise<Uint8Array>} - Decrypted document key
 */
export async function decryptShare(shareEncryptedDk, shareNonce, senderEphemeralPublic, recipientPrivateKey) {
  const senderPublic = fromBase64(senderEphemeralPublic);
  
  // Derive shared secret
  const sharedSecret = deriveSharedSecret(recipientPrivateKey, senderPublic);
  const shareKey = deriveKeyFromSharedSecret(sharedSecret);
  
  // Decrypt document key
  return await unwrapKey(shareEncryptedDk, shareKey, shareNonce);
}

/**
 * Create password-protected share for external users
 * 
 * @param {string} encryptedDocumentKey - Document key (encrypted by owner's MEK)
 * @param {string} dkNonce - Nonce for document key
 * @param {Uint8Array} ownerMek - Owner's MEK
 * @returns {Promise<Object>} - Share data and password to communicate separately
 */
export async function createExternalShare(encryptedDocumentKey, dkNonce, ownerMek) {
  // Unwrap the document key
  const documentKey = await unwrapKey(encryptedDocumentKey, ownerMek, dkNonce);
  
  // Generate random access password (12 characters, alphanumeric)
  const accessPasswordBytes = generateRandomBytes(9);
  const accessPassword = toBase64(accessPasswordBytes).replace(/[+/=]/g, '').substring(0, 12);
  
  // Derive share key from password
  const shareKey = sha256(new TextEncoder().encode('TerraKronos-External-' + accessPassword));
  
  // Encrypt document key with share key
  const { wrappedKey: shareEncryptedDk, nonce: shareNonce } = await wrapKey(documentKey, shareKey);
  
  return {
    shareData: {
      share_encrypted_dk: shareEncryptedDk,
      share_nonce: shareNonce
    },
    accessPassword // To communicate separately to recipient
  };
}

/**
 * Decrypt an external share using access password
 */
export async function decryptExternalShare(shareEncryptedDk, shareNonce, accessPassword) {
  const shareKey = sha256(new TextEncoder().encode('TerraKronos-External-' + accessPassword));
  return await unwrapKey(shareEncryptedDk, shareKey, shareNonce);
}

// ============================================
// Encryption Context (Session Management)
// ============================================

let _sessionMek = null;
let _sessionSharingPrivateKey = null;

/**
 * Store MEK in session (call after successful login)
 * WARNING: This keeps the key in memory. Clear on logout.
 */
export function setSessionKeys(mek, sharingPrivateKey = null) {
  _sessionMek = mek;
  _sessionSharingPrivateKey = sharingPrivateKey;
}

/**
 * Get MEK from session
 */
export function getSessionMek() {
  return _sessionMek;
}

/**
 * Get sharing private key from session
 */
export function getSessionSharingPrivateKey() {
  return _sessionSharingPrivateKey;
}

/**
 * Clear session keys (call on logout)
 */
export function clearSessionKeys() {
  if (_sessionMek) {
    _sessionMek.fill(0); // Overwrite memory
  }
  if (_sessionSharingPrivateKey) {
    _sessionSharingPrivateKey.fill(0);
  }
  _sessionMek = null;
  _sessionSharingPrivateKey = null;
}

/**
 * Check if encryption is unlocked for this session
 */
export function isEncryptionUnlocked() {
  return _sessionMek !== null;
}

/**
 * Ensure the X25519 sharing private key is in memory. If MEK is present but the
 * sharing key was never loaded (e.g. legacy setSessionKeys(mek) only), unwrap it
 * from the server blob using the current MEK.
 */
export async function ensureSessionSharingPrivateKey(authToken, apiBase) {
  let sk = getSessionSharingPrivateKey();
  if (sk) return sk;

  const mek = getSessionMek();
  if (!mek || !authToken) return null;

  const base = apiBase || '';
  const res = await fetch(`${base}/api/encryption/status`, {
    headers: { Authorization: `Bearer ${authToken}` }
  });
  if (!res.ok) return null;

  const serverData = await res.json();
  if (!serverData.encrypted_sharing_private_key || !serverData.sharing_key_nonce) {
    return null;
  }

  sk = await unwrapKey(
    serverData.encrypted_sharing_private_key,
    mek,
    serverData.sharing_key_nonce
  );
  setSessionKeys(mek, sk);
  return sk;
}

export default {
  // Utilities
  toBase64,
  fromBase64,
  generateRandomBytes,
  generateKey,
  generateNonce,
  generateSalt,
  
  // Key derivation
  deriveKeyFromPassword,
  
  // Encryption
  encrypt,
  decrypt,
  encryptString,
  decryptString,
  wrapKey,
  unwrapKey,
  
  // Recovery
  generateRecoveryCode,
  validateRecoveryCode,
  deriveKeyFromRecoveryCode,
  
  // Key exchange
  generateKeyPair,
  deriveSharedSecret,
  deriveKeyFromSharedSecret,
  
  // High-level operations
  initializeUserEncryption,
  unlockEncryption,
  recoverEncryption,
  reEncryptWithNewPassword,
  
  // Field encryption
  encryptField,
  decryptField,
  encryptFields,
  decryptFields,
  
  // Document encryption
  encryptDocument,
  decryptDocument,
  
  // Transaction Key management (Phase 2 E2EE)
  generateTransactionKey,
  unwrapTransactionKey,
  wrapTransactionKeyForUser,
  unwrapSharedTransactionKey,
  encryptDocumentWithTK,
  decryptDocumentWithTK,
  rewrapDocumentKeyFromMekToTk,
  
  // Sharing
  createShare,
  decryptShare,
  createExternalShare,
  decryptExternalShare,
  
  // External document sharing (Phase 4 E2EE)
  createExternalDocumentShare,
  decryptExternalDocumentShare,
  
  // Session
  setSessionKeys,
  getSessionMek,
  getSessionSharingPrivateKey,
  clearSessionKeys,
  isEncryptionUnlocked,
  ensureSessionSharingPrivateKey
};
